<template>
  <div
    class="fixed top-0 left-0 w-screen h-screen z-[100001] opacity-0 pointer-events-none transition-all duration-500"
    id="tutco-immediate-assistance-modal"
    :class="{ 'opacity-100 pointer-events-auto': state.isModalVisible }">
    <div
      class="absolute top-0 left-0 w-full h-full z-10 opacity-70 bg-black backdrop-blur-lg"
      @click="closeModal"></div>
    <div
      class="absolute t-1/2 left-0 -translate-x-full scale-0 w-[530px] max-w-full h-full z-20 flex items-center transition-all duration-500"
      :class="{ 'left-1/2 !-translate-x-1/2 scale-100': state.isModalVisible }">
      <div class="relative w-full h-fit max-h-full bg-white overflow-y-auto rounded-2xl">
        <div
          class="absolute top-2 right-2 z-10 cursor-pointer group"
          @click="closeModal">
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-5 h-auto">
            <path
              d="M22.9999 1.35556L1.00132 23.3542"
              stroke="#553F3F"
              stroke-width="2"
              stroke-linecap="round"
              class="transition-all duration-300 group-hover:stroke-red" />
            <path
              d="M22.9999 23.3554L1.00132 1.35678"
              stroke="#553F3F"
              stroke-width="2"
              stroke-linecap="round"
              class="transition-all duration-300 group-hover:stroke-red" />
          </svg>
        </div>
        <div class="w-full px-4 md:px-12 py-4 md:py-12">
          <div
            v-if="!state.isSuccessInfoVisible"
            class="w-full flex flex-col">
            <div class="w-full flex justify-center">
              <img
                :src="`${GLOBAL_CURRENT_COMPANY_LOGO}`"
                alt="TUTCO logo"
                title="TUTCO logo"
                class="lazy block w-auto max-w-96 h-auto max-h-20 mb-5" />
            </div>
            <div class="w-full text-3xl font-medium text-center mb-3">Is Immediate Assistance Required?</div>
            <div class="w-full text-lg font-light text-center mb-3">Please complete this form, and get the fastest delivery in industry.</div>
            <div class="w-full flex flex-col items-center gap-4">
              <div class="w-full">
                <InputText
                  :label="'Full Name*'"
                  id="immediate-assistance-full-name-input"
                  v-model:value="state.fullName"
                  :isRequired="true"
                  :maxLength="255"
                  :triggerValidation="state.triggerFormValidationCounter"
                  @check-is-valid="checkIsFullNameValid" />
              </div>
              <div class="w-full">
                <InputEmail
                  :label="'Email*'"
                  id="immediate-assistance-email-input"
                  v-model:value="state.email"
                  :isRequired="true"
                  :maxLength="254"
                  :triggerValidation="state.triggerFormValidationCounter"
                  @check-is-valid="checkIsEmailValid" />
              </div>
              <div class="w-full">
                <InputPhoneNumber
                  :label="'Phone*'"
                  id="immediate-assistance-phone-input"
                  v-model:value="state.phone"
                  :isRequired="true"
                  :maxLength="30"
                  :triggerValidation="state.triggerFormValidationCounter"
                  @check-is-valid="checkIsPhoneValid" />
              </div>
              <div class="w-full">
                <TextAreaComponent
                  :label="'Comments'"
                  id="immediate-assistance-comments-input"
                  v-model:value="state.comments"
                  :isRequired="false"
                  :maxLength="2000"
                  :triggerValidation="state.triggerFormValidationCounter"
                  @check-is-valid="checkIsCommentsValid" />
              </div>
              <div class="w-full flex justify-center">
                <div
                  class="w-44 h-9 flex items-center justify-center cursor-pointer rounded bg-red hover:bg-dark-red text-white transition-colors duration-300"
                  :class="{ 'pointer-events-none grayscale': state.isButtonDisabled }"
                  @click="sendFormData">
                  Contact Us
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="state.isSuccessInfoVisible"
            class="w-full">
            <div class="w-full text-3xl font-medium text-center text-red mb-4">Thanks!</div>
            <div class="w-full text-lg font-light text-center text-red mb-10">Someone from our sales team will reach out to you.</div>
            <div class="w-full flex justify-center">
              <img
                :src="`${GLOBAL_CURRENT_COMPANY_LOGO}`"
                alt="TUTCO logo"
                title="TUTCO logo"
                class="lazy block w-44 h-auto mb-5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import InputText from '@/components/website/form-components/InputText.vue';
import InputEmail from '@/components/website/form-components/InputEmail.vue';
import InputPhoneNumber from '@/components/website/form-components/InputPhoneNumber.vue';
import TextAreaComponent from '@/components/website/form-components/TextAreaComponent.vue';
import GlobalHelper from '@/helpers/global/global.helper';
import DataLayerHelper from '@/helpers/global/dataLayer.helper';

const state = reactive({
  isModalVisible: false as boolean,
  fullName: '' as string,
  isFullNameValid: false as boolean,
  email: '' as string,
  isEmailValid: false as boolean,
  phone: '' as string,
  isPhoneValid: false as boolean,
  comments: '' as string,
  isCommentsValid: true as boolean,
  triggerFormValidationCounter: 0 as number,
  isSuccessInfoVisible: false as boolean,
  isButtonDisabled: false as boolean,
});

//CK - cookie key
const GLOBAL_CURRENT_COMPANY_LOGO = CURRENT_COMPANY_LOGO;
const IA_MODAL_SUBMITTED_CK = 'IAModalVisibility_' + COMPANY_UID;
const IA_MODAL_SHOWS_COUNTER_CK = 'IAModalShowsCounter_' + COMPANY_UID;
const IA_MODAL_LATEST_SHOW_TS_CK = 'IAModalTimestamp_' + COMPANY_UID;

const initOpenModalButtonLogic = () => {
  const openModalButton = document.querySelector('.tutco-immediate-assistance-modal-open-button');

  if (openModalButton !== null) {
    openModalButton.classList.add('opacity-100', 'pointer-events-auto');

    openModalButton.addEventListener('click', () => {
      state.isModalVisible = true;
    });
  }
};

const showModal = () => {
  if (typeof IS_POPUP_VISIBLE === 'undefined' || Boolean(IS_POPUP_VISIBLE)) {
    if (Boolean(IS_DUCT_CONDUCT_POPUP_VISIBLE) !== Boolean(IS_FARNAM_POPUP_VISIBLE)) {
      initOpenModalButtonLogic();

      const popupTimeout = typeof POPUP_DELAY_SECONDS === 'number' ? POPUP_DELAY_SECONDS * 1000 : 7000;

      const resetPeriodInHours = POPUP_RESET_PERIOD_IN_HOURS ? POPUP_RESET_PERIOD_IN_HOURS : 24;
      const resetPeriodInMilliSeconds = resetPeriodInHours * 60 * 60 * 1000;

      if (GlobalHelper.readCookie(IA_MODAL_LATEST_SHOW_TS_CK) === null) {
        GlobalHelper.setCookie(IA_MODAL_LATEST_SHOW_TS_CK, String(Date.now()));
      }
      if (GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK) === null) {
        GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, '1');
      }

      let isTimestampNotPassed = Number(GlobalHelper.readCookie(IA_MODAL_LATEST_SHOW_TS_CK)) + resetPeriodInMilliSeconds > Date.now();
      let isMaxShowsCounterReached = Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) <= POPUP_SHOW_LIMIT_PER_PERIOD;
      const formIsNotSubmitted = GlobalHelper.readCookie(IA_MODAL_SUBMITTED_CK) === null;

      if (!isTimestampNotPassed) {
        isTimestampNotPassed = isMaxShowsCounterReached = true;
      }

      if (isTimestampNotPassed && isMaxShowsCounterReached && formIsNotSubmitted) {
        setTimeout(() => {
          if (Number(GlobalHelper.readCookie(IA_MODAL_LATEST_SHOW_TS_CK)) + resetPeriodInMilliSeconds < Date.now()) {
            GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, '1');
            GlobalHelper.setCookie(IA_MODAL_LATEST_SHOW_TS_CK, String(Date.now()));
          }

          GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, String(Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) + 1));
          state.isModalVisible = true;
        }, popupTimeout);
      }
    } else if (Boolean(IS_DUCT_CONDUCT_POPUP_VISIBLE) && Boolean(IS_FARNAM_POPUP_VISIBLE)) {
      console.log("2 modals can't be shown");
    }
  }
};

const openModalWithButton = () => {
  state.isModalVisible = true;
};

const closeModal = () => {
  state.isModalVisible = false;
};

const checkIsFullNameValid = (value: boolean) => {
  state.isFullNameValid = value;
};

const checkIsEmailValid = (value: boolean) => {
  state.isEmailValid = value;
};

const checkIsPhoneValid = (value: boolean) => {
  state.isPhoneValid = value;
};

const checkIsCommentsValid = (value: boolean) => {
  state.isCommentsValid = value;
};

const sendFormData = () => {
  state.triggerFormValidationCounter++;

  setTimeout(() => {
    if (state.isFullNameValid && state.isEmailValid && state.isPhoneValid && state.isCommentsValid) {
      state.isButtonDisabled = true;
      const data = new FormData();

      if (IS_DUCT_CONDUCT_POPUP_VISIBLE) {
        data.append('xnQsjsdp', '1b64a1c789b48be936008806867bfda9b30bd690d9d462705f7a67af5e6f6c41');
        data.append('xmIwtLD', '23469143a30a258092cd22a8e5ae38b542c2bd5075738124b3cc4c21cf8686cda53ca51a2dc2a899753aab924f865fab');
        data.append('LEADCF26', state.comments);
      }

      if (IS_FARNAM_POPUP_VISIBLE) {
        data.append('xnQsjsdp', 'fd98b871d72e885d415b94e55e94d007badbb0a2f063bf4c0fdf60267f684c3e');
        data.append('xmIwtLD', '6f94f1ccf1c75a5d740d28988ed3b98ff182d798720ba37be59e610b853359aa5c765ec35ba0f3074aa6e7471fdfe35f');
        data.append('LEADCF10', state.comments);
      }

      data.append('zc_gad', '');
      data.append('actionType', 'TGVhZHM=');
      data.append('returnURL', 'null');
      data.append('Last Name', state.fullName);
      data.append('LEADCF28', location.protocol + '//' + location.host + location.pathname);
      data.append('Lead Source', 'Popup-form');
      data.append('Email', state.email);
      data.append('Phone', state.phone);

      fetch('https://crm.zoho.com/crm/WebToLeadForm', {
        method: 'POST',
        body: data,
      }).then((response) => {
        if (response.status === 200) {
          state.isSuccessInfoVisible = true;
          GlobalHelper.setCookie(IA_MODAL_SUBMITTED_CK, '1', 24);
          DataLayerHelper.send('pop_up_form_submit', 'Lead Generation', 'Submit button', 'Pop up form', 1);
          state.isButtonDisabled = false;
        }
      });
    }
  }, 100);
};

onMounted(() => {
  showModal();
});
</script>

<style lang="scss" scoped></style>
